import { default as _get } from 'lodash/get'
import formatDate from 'utils/dates/formatDate'
import regEx from 'utils/global/regEx'
import type { Locale } from '@messenger/types'
import { escapeHtml, sanitizeCaseRichTextField } from '@messenger/utils'
import { formFieldKeys } from '@messenger/consts'

type ReplacementData = Record<string, unknown>

type DateFieldName =
  | 'date_of_birth'
  | 'date_of_death'
  | 'date_of_marriage'
  | 'interment_date'

// this utility replaces any values between squiggly brackets: {$VALUE}
// with its corresponding value determined by the "keys" object
const keySubstring = (key: string): string => key.substring(2, key.length - 1)

/** The case fields that hold rich text, written through the case write's per-field step. */
const RICH_TEXT_KEYS = new Set(['obituary', formFieldKeys.orderOfService])

/**
 * A key's value as markup: a rich-text field as the per-field step's HTML
 * (`sanitizeCaseRichTextField`), a value the caller built as HTML
 * (`htmlValues`, such as the name wrapper whose parts it escaped one by one)
 * as it is, and every other value escaped as text.
 */
const markupOf = (
  keyName: string,
  value: unknown,
  htmlValues: Record<string, string>,
): string => {
  if (Object.prototype.hasOwnProperty.call(htmlValues, keyName))
    return htmlValues[keyName]
  if (RICH_TEXT_KEYS.has(keyName))
    return sanitizeCaseRichTextField(String(value)).html
  return escapeHtml(String(value))
}

const replaceItems = <T extends ReplacementData>(
  html: string,
  keys: T,
  htmlValues: Record<string, string>,
) =>
  html?.replace(regEx.dollarCurlyBraces, (key) => {
    const keyName = keySubstring(key)
    const value = _get(keys, keyName)
    if (!value && !htmlValues[keyName]) return '\u200A'
    return markupOf(keyName, value, htmlValues)
  })

const replaceItemsWithDates = <T extends ReplacementData>(
  html: string,
  keys: T,
  dateFormat: string,
  locale: Locale,
  htmlValues: Record<string, string>,
) => {
  const dateFields: DateFieldName[] = [
    'date_of_birth',
    'date_of_death',
    'date_of_marriage',
    'interment_date',
  ]

  return html?.replace(regEx.dollarCurlyBraces, (key) => {
    const keyName = keySubstring(key)
    let value

    const isDateField = dateFields.includes(keyName as DateFieldName)
    if (isDateField) {
      const dateValue = _get(keys, keyName)

      if (!dateValue) {
        return '\u200A'
      }

      const shouldFormatDate =
        dateValue instanceof Date ||
        (typeof dateValue === 'string' && dateValue.length > 0)

      if (shouldFormatDate) {
        value = formatDate(dateValue, dateFormat, false, locale)
      } else {
        value = String(dateValue)
      }
    } else {
      value = _get(keys, keyName)
    }

    if (!value && !htmlValues[keyName]) return '\u200A'
    return markupOf(keyName, value, htmlValues)
  })
}

export const replaceInitialValues = <T extends ReplacementData>(
  content: string,
  keys: T,
  htmlValues: Record<string, string> = {},
) => {
  try {
    return replaceItems(content, keys, htmlValues)
  } catch (error) {
    console.error('Error replaceInitialValues:', error)
    return ''
  }
}

export const replaceInitialValuesAndDates = <T extends ReplacementData>(
  content: string,
  keys: T,
  dateFormat: string,
  locale: Locale,
  htmlValues: Record<string, string> = {},
) => {
  try {
    return replaceItemsWithDates(content, keys, dateFormat, locale, htmlValues)
  } catch (error) {
    console.error('Error replaceInitialValuesAndDates:', error)
    return ''
  }
}
